import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { finalize, shareReplay, tap } from 'rxjs/operators';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import { SubscriptionService } from 'src/app/private/services/subscription.service';
import {
  clearActiveUserPackageCache,
  readActiveUserPackageCache,
} from 'src/app/utils/active-user-package-cache.util';

/**
 * When the active package API reports certain reminder types (or no subscription),
 * the app should only allow Billing & Subscription.
 */
const BILLING_ONLY_REMINDER_TYPES = new Set([
  'NO_SUBSCRIPTION',
  'SUBSCRIPTION_EXPIRED',
  'TRIAL_EXPIRED',
]);

@Injectable({
  providedIn: 'root'
})
export class SubscriptionBillingLockService {
  private readonly _billingOnly = new BehaviorSubject<boolean>(false);
  readonly billingOnly$ = this._billingOnly.asObservable();

  private lastFetchAt = 0;
  private lastRawResponse: any = null;
  private inFlight$: Observable<any> | null = null;

  constructor(
    private readonly subscriptionService: SubscriptionService,
    private readonly localStorageService: LocalStorageService
  ) {}

  isBillingRouteLockActive(): boolean {
    return this._billingOnly.value;
  }

  /** Reset in-memory lock only (keeps localStorage cache for fast post-login navigation). */
  resetLockState(): void {
    this._billingOnly.next(false);
    this.lastFetchAt = 0;
    this.lastRawResponse = null;
    this.inFlight$ = null;
  }

  clear(): void {
    this.resetLockState();
    const clientId = this.localStorageService.getClientId();
    if (clientId) {
      clearActiveUserPackageCache(clientId);
    } else {
      clearActiveUserPackageCache();
    }
  }

  applyActivePackageResponse(res: any): void {
    const data = res?.data !== undefined ? res.data : res;
    if (data == null) {
      this._billingOnly.next(true);
      return;
    }
    const reminders = data?.reminders;
    const locked =
      Array.isArray(reminders) &&
      reminders.some(
        (r: any) =>
          r?.type != null && BILLING_ONLY_REMINDER_TYPES.has(String(r.type))
      );
    this._billingOnly.next(locked);
  }

  /** Apply billing lock from login / verifyOtp response (checked server-side). */
  applyLoginSubscriptionLock(loginResponse: any): void {
    if (loginResponse?.billing_route_lock === true) {
      this._billingOnly.next(true);
    }
  }

  /**
   * Loads package status (cached in localStorage for 24h) and updates billing lock state.
   * Uses cached data immediately when available, then refreshes in the background.
   */
  ensureActivePackageForClient(): Observable<any> {
    const clientId = this.localStorageService.getClientId();
    if (!clientId) {
      this.clear();
      return of(null);
    }

    const cached = readActiveUserPackageCache(clientId);
    if (cached != null) {
      this.lastRawResponse = cached;
      this.lastFetchAt = Date.now();
      this.applyActivePackageResponse(cached);
      this.refreshActivePackageInBackground(clientId);
      return of(cached);
    }

    return this.fetchActivePackage(clientId);
  }

  private fetchActivePackage(clientId: string | number): Observable<any> {
    if (this.inFlight$) {
      return this.inFlight$;
    }

    this.inFlight$ = this.subscriptionService
      .getCurrentUserActivePackage({ client_id: clientId })
      .pipe(
        tap((res) => {
          this.lastRawResponse = res;
          this.lastFetchAt = Date.now();
          this.applyActivePackageResponse(res);
        }),
        finalize(() => {
          this.inFlight$ = null;
        }),
        shareReplay(1)
      );

    return this.inFlight$;
  }

  private refreshActivePackageInBackground(clientId: string | number): void {
    if (this.inFlight$) {
      return;
    }

    this.fetchActivePackage(clientId).subscribe({
      error: () => {
        /* keep cached lock state on refresh failure */
      },
    });
  }

  /** Drop cache and refetch (e.g. after successful renewal on billing page). */
  forceRefresh(): Observable<any> {
    const clientId = this.localStorageService.getClientId();
    if (!clientId) {
      this.clear();
      return of(null);
    }

    this.inFlight$ = null;
    clearActiveUserPackageCache(clientId);

    return this.subscriptionService
      .getCurrentUserActivePackage({ client_id: clientId }, { forceRefresh: true })
      .pipe(
        tap((res) => {
          this.lastRawResponse = res;
          this.lastFetchAt = Date.now();
          this.applyActivePackageResponse(res);
        })
      );
  }
}
